import React, { useEffect, useState } from "react";
import { getAlertsStatus, sendTestAlert } from "@/lib/apiClient";
import { toast } from "sonner";
import { BellRing, Loader2, Plus, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";

export const AlertSettings = ({ s, setS, onSave, saving }) => {
  const { users } = useApp();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => { getAlertsStatus().then(setStatus); }, [s.alert_emails]);

  const add = () => {
    const v = email.trim().toLowerCase();
    if (!v) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return toast.error("Enter a valid email address");
    if ((s.alert_emails || []).includes(v)) return toast.error("Already added");
    setS({ ...s, alert_emails: [...(s.alert_emails || []), v] });
    setEmail("");
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await sendTestAlert();
      toast.success(`Test alert sent to ${r.recipients.join(", ")}`);
      getAlertsStatus().then(setStatus);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test alert");
    }
    setTesting(false);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6 max-w-2xl" data-testid="alert-settings">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500">
            <BellRing className="h-5 w-5" />
          </span>
          <div>
            <div className="font-medium">Ingestion alerts</div>
            <div className="text-sm text-muted-foreground">
              Email the finance team when Gmail ingestion fails, when auto-check switches off after repeated failures, and when it recovers.
              Max one email per hour per alert type.
            </div>
          </div>
        </div>
        <Switch data-testid="alerts-toggle" checked={!!s.alerts_enabled} onCheckedChange={(v) => setS({ ...s, alerts_enabled: v })} />
      </div>

      <div className="flex items-center justify-between gap-4 mb-4 rounded-lg border border-border bg-muted/20 px-4 py-3">
        <div>
          <div className="text-sm font-medium">Forwarding address alerts</div>
          <div className="text-xs text-muted-foreground">Email straight away when a forwarded email can't be read, plus an hourly summary of unknown senders waiting for approval.</div>
        </div>
        <Switch data-testid="forwarding-alerts-toggle" checked={s.forwarding_alerts !== false} onCheckedChange={(v) => setS({ ...s, forwarding_alerts: v })} />
      </div>

      <div className="flex items-center justify-between gap-4 mb-4 rounded-lg border border-border bg-muted/20 px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-medium">Exchange-rate movement alerts</div>
          <div className="text-xs text-muted-foreground">Email when a foreign-currency invoice is converted at a rate more than
            <Input data-testid="fx-alert-threshold" type="number" min="0.1" max="50" step="0.5" value={s.fx_alert_threshold_pct ?? 3}
                   onChange={(e) => setS({ ...s, fx_alert_threshold_pct: Number(e.target.value) })} className="mx-1.5 inline-block h-6 w-16 px-1.5 text-xs font-mono align-middle" />
            % away from the supplier's usual rate (median of their last 12 invoices).</div>
        </div>
        <Switch data-testid="fx-alerts-toggle" checked={s.fx_alerts !== false} onCheckedChange={(v) => setS({ ...s, fx_alerts: v })} />
      </div>

      <div className="flex gap-2">
        <Input data-testid="alert-email-input" type="email" placeholder="finance@yourcompany.com" value={email}
               onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} className="font-mono text-sm" />
        <Button data-testid="alert-email-add" variant="outline" onClick={add} disabled={!email.trim()}><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="alert-email-list">
        {(s.alert_emails || []).length === 0 && <span className="text-xs text-muted-foreground" data-testid="alert-email-empty">No recipients yet — alerts will not be sent.</span>}
        {(s.alert_emails || []).map((e) => (
          <span key={e} data-testid={`alert-email-chip-${e}`} className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs font-mono">
            {e}
            <button data-testid={`alert-email-remove-${e}`} onClick={() => setS({ ...s, alert_emails: s.alert_emails.filter((x) => x !== e) })} className="text-muted-foreground hover:text-red-500 ml-0.5"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>

      {status && !status.configured && (
        <p className="mt-3 text-xs text-amber-500" data-testid="alerts-not-configured">Email service is not configured on the server — alerts will be logged but not delivered.</p>
      )}
      {status?.recent?.length > 0 && (
        <div className="mt-4 text-xs text-muted-foreground" data-testid="alerts-recent">
          Last alert: <span className="text-foreground">{status.recent[0].subject}</span> → {status.recent[0].to} · {new Date(status.recent[0].created_at).toLocaleString()} ·{" "}
          <span className={status.recent[0].status === "sent" ? "text-emerald-500" : "text-red-500"}>{status.recent[0].status}</span>
        </div>
      )}

      <div className="border-t border-border pt-4 mt-4" data-testid="sla-settings">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Review deadline (SLA)</div>
            <div className="text-xs text-muted-foreground">Flagged invoices nobody has decided on within this many hours get an @mention nudge (max one per day) and appear as "SLA breached".</div>
          </div>
          <div className="flex items-center gap-2">
            <Input data-testid="sla-hours-input" type="number" min="1" max="720" className="w-24 font-mono" value={s.review_sla_hours ?? 48}
                   onChange={(e) => setS({ ...s, review_sla_hours: Number(e.target.value) })} />
            <span className="text-xs text-muted-foreground">hours</span>
          </div>
        </div>
        <label className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          Also email the alert recipients when deadlines are missed
          <Switch data-testid="sla-email-toggle" checked={!!s.sla_nudge_email} onCheckedChange={(v) => setS({ ...s, sla_nudge_email: v })} />
        </label>
        <div className="mt-4 rounded-lg border border-border bg-muted/30 p-3" data-testid="escalation-settings">
          <div className="text-sm font-medium">Escalation</div>
          <div className="text-xs text-muted-foreground mt-0.5">If nobody decides within the hours below after the first reminder, the invoice is re-assigned to the escalation reviewer (both are @mentioned). If it's already theirs, it goes to the next teammate.</div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">Escalate after
              <Input data-testid="escalation-hours-input" type="number" min="1" max="720" className="w-20 font-mono" value={s.escalation_after_hours ?? 24}
                     onChange={(e) => setS({ ...s, escalation_after_hours: Number(e.target.value) })} /> hours to
            </div>
            <select data-testid="escalation-reviewer-select" value={s.escalation_reviewer || users[0]} onChange={(e) => setS({ ...s, escalation_reviewer: e.target.value })}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm">
              {users.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-4 mt-4 flex flex-wrap justify-end gap-2">
        <Button data-testid="alert-test-btn" variant="ghost" onClick={test} disabled={testing || !(s.alert_emails || []).length}>
          {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Send test alert
        </Button>
        <Button data-testid="save-alerts-btn" variant="outline" onClick={() => onSave("Alert settings saved")} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save Alert Settings
        </Button>
      </div>
    </div>
  );
};
